import { Injectable } from '@angular/core';
import { CreateQuestionnaireRequestInput, QuestionnaireType } from 'src/API';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { Clinic } from 'src/app/core/api/clinic.service';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { FormTemplateSchema } from 'src/app/form-template/form-template-model';
import { ClinicianPrefillPatch, QuestionnaireRequestConfig } from './consent-form.util';
import { ConsentSendService } from './consent-send.service';
import { Patient } from './patient.service';
import { canSendAsMultiQuestionnaire } from './questionnaire-request-utils';
import { QuestionnaireRequestService } from './questionnaire-request.service';

export interface QuestionnaireSendOptions {
  sendToEmail: boolean;
  sendToPhone: boolean;
  language: string;
  /** Groups several questionnaires sent to one patient into a single patient-facing flow. */
  groupId?: string;
  clinicianPrefill?: ClinicianPrefillPatch;
  /** Falls back to the logged-in clinic's country when omitted. */
  clinicCountry?: string;
}

/**
 * Builds the CreateQuestionnaireRequestInput for one patient + one questionnaire key, whatever the
 * key's flavour: a standard QuestionnaireType, a custom questionnaire, an intake form or a consent
 * form. Both the single-patient send dialog and the multi-patient one go through here, so the
 * schema/type/config rules cannot drift between them.
 */
@Injectable({
  providedIn: 'root'
})
export class QuestionnaireSendBuilderService {
  constructor(
    private questionnaireRequestService: QuestionnaireRequestService,
    private clinicSetupService: ClinicSetupService,
    private intakeFormService: IntakeFormService,
    private consentSendService: ConsentSendService
  ) {}

  /** Every key the picker can offer, mapped to the schema stored on the request's config. */
  public getKeyToSchemaMap(): { [key: string]: FormTemplateSchema } {
    return {
      ...this.questionnaireRequestService.allQuestionnairesAndIntakeFormsKeyToSchemaMap,
      ...this.getIntakeFormKeyToSchemaMap(),
      ...this.consentSendService.getConsentFormKeyToSchemaMap()
    };
  }

  public resolveSchemaForKey(questionnaireKey: string): FormTemplateSchema | undefined {
    if (!questionnaireKey) {
      return undefined;
    }
    return this.getKeyToSchemaMap()[questionnaireKey];
  }

  /** Default intake templates are wrapped ({ schema }); custom ones are bare schemas. */
  public getIntakeFormKeyToSchemaMap(): { [key: string]: FormTemplateSchema } {
    return Object.entries({
      ...this.intakeFormService.defaultIntakeFormTemplateSchemas,
      ...this.questionnaireRequestService.customIntakeFormKeyToSchemaMap
    }).reduce((keyToSchema, [key, value]) => {
      keyToSchema[key] = value['schema'] ? value['schema'] : value;
      return keyToSchema;
    }, {});
  }

  public buildQuestionnaireRequestInput(
    patient: Partial<Patient>,
    questionnaireKey: string,
    options: QuestionnaireSendOptions
  ): CreateQuestionnaireRequestInput {
    const baseInput: CreateQuestionnaireRequestInput = this.questionnaireRequestService.createFormGroup(
      {
        questionnaireType: questionnaireKey as QuestionnaireType,
        patient: {
          ...patient,
          nonModifiableData: this.normalizeNonModifiableData(patient),
          clinic: { country: this.resolveClinicCountry(patient, options) } as Clinic
        }
      }
    ).value;

    const questionnaireRequestInput: CreateQuestionnaireRequestInput = {
      ...baseInput,
      // A channel the caller turned off is dropped entirely: an empty string here would still be
      // treated as a delivery address by the backend.
      email: options.sendToEmail && !!baseInput.email ? baseInput.email : undefined,
      mobile: options.sendToPhone && !!baseInput.mobile ? baseInput.mobile : undefined,
      language: options.language,
      ...(canSendAsMultiQuestionnaire(questionnaireKey) && { groupId: options.groupId })
    };

    return this.applyQuestionnaireSchemaToInput(
      questionnaireRequestInput,
      questionnaireKey,
      options.clinicianPrefill
    );
  }

  /**
   * Stamps the resolved `type` and the serialized `config` (schema, expiry, clinic branding,
   * clinician prefill) onto an already-built request input. Callers that collect email/mobile from
   * their own form - the single-patient dialog - use this instead of building from the patient.
   */
  public applyQuestionnaireSchemaToInput(
    questionnaireRequestInput: CreateQuestionnaireRequestInput,
    questionnaireKey: string,
    clinicianPrefill?: ClinicianPrefillPatch
  ): CreateQuestionnaireRequestInput {
    const schema = this.resolveSchemaForKey(questionnaireKey);
    const questionnaireRequestConfig: QuestionnaireRequestConfig = {
      questionnaireExpiry: this.clinicSetupService.clinicSetupFormGroup.value.questionnaireExpiry
    };
    const stampedInput: CreateQuestionnaireRequestInput = { ...questionnaireRequestInput };

    if (schema) {
      // Anything outside the built-in questionnaire library rides as CUSTOM with its schema in
      // config - that is how custom questionnaires, intake forms and consent forms are stored.
      stampedInput.type = this.questionnaireRequestService.formTemplateLibraryQuestionnaires[
        questionnaireKey
      ]
        ? (questionnaireKey as QuestionnaireType)
        : QuestionnaireType.CUSTOM;

      questionnaireRequestConfig.schema = schema;

      const clinic = this.clinicSetupService.clinic;
      if (clinic) {
        questionnaireRequestConfig.clinicBranding = {
          name: clinic.name,
          logo: clinic.logo || null
        };
      }

      if (clinicianPrefill) {
        questionnaireRequestConfig.clinicianPrefill = clinicianPrefill;
      }
    }

    stampedInput.config = JSON.stringify(questionnaireRequestConfig);
    return stampedInput;
  }

  private resolveClinicCountry(
    patient: Partial<Patient>,
    options: QuestionnaireSendOptions
  ): string {
    if (options.clinicCountry) {
      return options.clinicCountry;
    }
    if (patient && patient.clinic && patient.clinic.country) {
      return patient.clinic.country;
    }
    const clinic = this.clinicSetupService.clinic;
    return (clinic && clinic.country) || '';
  }

  /** Patients loaded through search carry this parsed; the API expects the JSON string. */
  private normalizeNonModifiableData(patient: Partial<Patient>): string {
    const nonModifiableData = patient && patient.nonModifiableData;
    if (!nonModifiableData) {
      return '{}';
    }
    return typeof nonModifiableData === 'string'
      ? nonModifiableData
      : JSON.stringify(nonModifiableData);
  }
}
